import { Alert } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import {
  PanelHeader,
  PanelTitle,
  PanelDetails
} from 'components/attacks/details/AttackStyles'
import Section from 'components/common/Section'
import { type ConcurrentExecutionNodeOffCanvasOutletContext } from 'components/scenarios/concurrent/ConcurrentExecutionNodeOffCanvas'
import { ConcurrentObserversTreeView } from 'components/scenarios/concurrent/ConcurrentObserversTreeView'
import { ConcurrentTreeView } from 'components/scenarios/concurrent/ConcurrentTreeView'
import { GraphEditorContextProvider } from 'components/scenarios/concurrent/GraphEditorContext'
import { findRunStep } from 'components/scenarios/utils/graph/legacy-graph'
import { generateGraphNodePathMap } from 'components/scenarios/utils/graph/metadata'
import theme from 'theme'

import type { ScenarioPreview } from 'types/api/reliability-tests/preview'
import type { ScenarioRunResponse } from 'types/api/scenarios/runs/scenario-runs'
import type { GraphNode, Scenario } from 'types/api/scenarios/scenario'

export const ConcurrentExecution: React.FC<{
  run?: ScenarioRunResponse
  scenario: Pick<Scenario, 'graph'>
  scenarioPreview?: ScenarioPreview
  showTitle?: boolean
  onAttackDetailsClose?(): void
  onAttackDetailsOpen?(node: GraphNode): void
}> = ({
  run,
  scenario,
  scenarioPreview,
  showTitle,
  onAttackDetailsClose,
  onAttackDetailsOpen
}) => {
  const { nodeId: nodeIdFromRouter, step } = useParams()

  // The app supports routing from two different path segments:
  // `nodes/:nodeId` | `steps/:step` in order to open the node details drawer.
  const nodeId = nodeIdFromRouter ?? step
  const selectedNode = run && nodeId ? findRunStep(run, nodeId) : undefined

  const { graph } = run ?? scenario
  const graphNodePaths = generateGraphNodePathMap(graph)
  const reason =
    run?.stage_info?.stageMetadata?.failedReason ??
    run?.stage_info?.stageMetadata?.haltReason

  const handleCloseDrawer = useCallback(() => {
    if (!selectedNode) return

    onAttackDetailsClose?.()
  }, [selectedNode, onAttackDetailsClose])

  const handleSelectNode = useCallback(
    (node: GraphNode) => {
      onAttackDetailsOpen?.(node)
    },
    [onAttackDetailsOpen]
  )

  const contextValue = useMemo<ConcurrentExecutionNodeOffCanvasOutletContext>(
    () => ({
      run,
      selectedNode,
      onClose: handleCloseDrawer
    }),
    [run, selectedNode, handleCloseDrawer]
  )

  return (
    <Section data-test-scenario-details-attack-configuration>
      {reason && (
        <Alert severity="error" sx={{ width: '100%' }}>
          {reason}
        </Alert>
      )}
      {showTitle && (
        <PanelHeader>
          <PanelTitle>Scenario Execution</PanelTitle>
        </PanelHeader>
      )}
      <PanelDetails css={{ marginTop: theme.space[0] }}>
        <GraphEditorContextProvider
          graph={graph}
          graphNodePaths={graphNodePaths}
        >
          {Boolean(graph?.observers?.length) && (
            <ConcurrentObserversTreeView
              css={{ marginBottom: theme.space[2] }}
              graph={graph}
              isClickable={Boolean(run)}
              onClick={handleSelectNode}
            />
          )}
          <ConcurrentTreeView
            hasRun={Boolean(run)}
            isClickable={Boolean(run)}
            scenarioPreview={scenarioPreview}
            onClick={handleSelectNode}
          />
        </GraphEditorContextProvider>
        {/* Node Details Drawer */}
        <Outlet context={contextValue} />
      </PanelDetails>
    </Section>
  )
}
